import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createRecord, updateRecord } from '../../services/recordStorageService';
import { TabbedModal } from './TabbedModal';
import { usePageLayout } from '../../hooks/useObjectManager';
import { EditSection } from '../edit/EditSection';
import { useToast } from '../../contexts/ToastContext';
import { ListViewRecord } from '../../types/listView';
import { User, Calendar, Link2 } from 'lucide-react';
import { validateRecord, type ValidationError } from '../../services/queries/validationRuleQueries';
import { ValidationErrors } from '../common/ValidationErrors';
import { RelatedRecordsTab } from '../tabs/RelatedRecordsTab';
import { ActivityTab } from '../tabs/ActivityTab';

interface LeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  lead?: ListViewRecord | null; // If provided, edit mode. If null, create mode
}

export const LeadModal: React.FC<LeadModalProps> = ({
  isOpen,
  onClose,
  lead
}) => {
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();
  const isEditMode = !!lead;

  // Get page layout for this lead
  const { data: pageLayout, isLoading: loadingLayout } = usePageLayout('Lead__c', lead);

  console.log('📝 Lead Modal - Page Layout:', {
    leadId: lead?.id,
    layoutName: pageLayout?.name,
    sectionCount: pageLayout?.sections?.length
  });

  // Define tabs based on mode
  // PE-1458: Removed Notes tab - notes consolidated into Activity Timeline
  const tabs = isEditMode
    ? [
        // EDIT MODE: All tabs (minus Notes per PE-1458)
        { id: 'overview', label: 'Overview', icon: User },
        { id: 'related', label: 'Related', icon: Link2 },
        { id: 'activity', label: 'Activity', icon: Calendar }
      ]
    : [
        // CREATE MODE: Overview only
        { id: 'overview', label: 'Overview', icon: User }
      ];

  // Form state - use Gobodo-style field names to match EditSection/useSectionFields
  const [formData, setFormData] = useState<Record<string, any>>({
    Name__c: '',
    Title__c: '',
    Company__c: '',
    Email__c: '',
    Phone__c: '',
    Status__c: 'New',
    Rating__c: 'Warm',
    LeadSource__c: 'Website',
    Industry__c: '',
    NumberOfEmployees__c: '',
    AnnualRevenue__c: '',
    Owner__c: 'John Smith',
    Description__c: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [validationRuleErrors, setValidationRuleErrors] = useState<ValidationError[]>([]);

  // Populate form in edit mode
  useEffect(() => {
    if (lead) {
      // Support both Gobodo-style (Name__c) and camelCase (name) field names from record
      setFormData({
        Name__c: (lead.Name__c || lead.name) as string || '',
        Title__c: (lead.Title__c || lead.title) as string || '',
        Company__c: (lead.Company__c || lead.company) as string || '',
        Email__c: (lead.Email__c || lead.email) as string || '',
        Phone__c: (lead.Phone__c || lead.phone) as string || '',
        Status__c: (lead.Status__c || lead.status) as string || 'New',
        Rating__c: (lead.Rating__c || lead.rating) as string || 'Warm',
        LeadSource__c: (lead.LeadSource__c || lead.leadSource) as string || 'Website',
        Industry__c: (lead.Industry__c || lead.industry) as string || '',
        NumberOfEmployees__c: (lead.NumberOfEmployees__c || lead.numberOfEmployees) as string || '',
        AnnualRevenue__c: (lead.AnnualRevenue__c || lead.annualRevenue) as string || '',
        Owner__c: (lead.Owner__c || lead.owner) as string || 'John Smith',
        Description__c: (lead.Description__c || lead.description) as string || ''
      });
    } else {
      // Reset form for create mode
      setFormData({
        Name__c: '',
        Title__c: '',
        Company__c: '',
        Email__c: '',
        Phone__c: '',
        Status__c: 'New',
        Rating__c: 'Warm',
        LeadSource__c: 'Website',
        Industry__c: '',
        NumberOfEmployees__c: '',
        AnnualRevenue__c: '',
        Owner__c: 'John Smith',
        Description__c: ''
      });
    }
    setErrors({});
    setValidationRuleErrors([]);
  }, [lead, isOpen]);

  // Handle field changes (for dynamic fields)
  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Handle change events (legacy support)
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    handleFieldChange(name, value);
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    // Check Gobodo-style field names
    const name = formData.Name__c || '';
    const company = formData.Company__c || '';
    const email = formData.Email__c || '';

    if (!name.trim()) {
      newErrors.Name__c = 'Lead name is required';
    }

    if (!company.trim()) {
      newErrors.Company__c = 'Company is required';
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.Email__c = 'Invalid email format';
    }

    console.log('[LeadModal] Validation:', { name, company, email, errors: newErrors });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      // Data is already in Gobodo field format (Name__c, Company__c, etc.)
      const gobodoData: Record<string, any> = {
        Name__c: data.Name__c,
        Title__c: data.Title__c,
        Company__c: data.Company__c,
        Email__c: data.Email__c,
        Phone__c: data.Phone__c,
        Status__c: data.Status__c,
        Rating__c: data.Rating__c,
        LeadSource__c: data.LeadSource__c,
        Industry__c: data.Industry__c,
        NumberOfEmployees__c: data.NumberOfEmployees__c ? parseInt(String(data.NumberOfEmployees__c), 10) : null,
        AnnualRevenue__c: data.AnnualRevenue__c ? parseFloat(String(data.AnnualRevenue__c)) : null,
        Owner__c: data.Owner__c,
        Description__c: data.Description__c,
        LastContact__c: new Date().toISOString()
      };

      // Remove null/empty values
      Object.keys(gobodoData).forEach(key => {
        if (gobodoData[key] === null || gobodoData[key] === '' || gobodoData[key] === undefined) {
          delete gobodoData[key];
        }
      });

      if (isEditMode && lead) {
        const updated = await updateRecord('Lead__c', String(lead.id), gobodoData);
        if (!updated) throw new Error('Failed to update lead');
        return { ...lead, ...data, id: updated.id };
      } else {
        const created = await createRecord('Lead__c', gobodoData);
        return { ...data, id: created.id };
      }
    },
    onSuccess: (data) => {
      // Invalidate lead queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['leadListViews'] });
      queryClient.invalidateQueries({ queryKey: ['leadRecords'] });
      queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Lead__c'] });
      queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Lead'] });
      if (isEditMode) {
        queryClient.invalidateQueries({ queryKey: ['lead', lead?.id] });
      }

      const leadName = (data as any).Name__c || 'Lead';
      success(
        isEditMode ? 'Lead updated' : 'Lead created',
        isEditMode ? `${leadName} has been updated` : `${leadName} has been created`
      );
      onClose();
    },
    onError: (error) => {
      console.error('[LeadModal] Save error:', error);
      showError('Error', 'Failed to save lead. Please try again.');
    }
  });

  const handleSubmit = (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    console.log('[LeadModal] handleSubmit called with formData:', formData);

    // Clear previous validation rule errors
    setValidationRuleErrors([]);

    // Run basic form validation first
    if (!validate()) {
      console.log('[LeadModal] Validation failed');
      showError('Validation Error', 'Please fix the errors before submitting');
      return;
    }
    console.log('[LeadModal] Validation passed, proceeding with save');

    // Run validation rules enforcement
    const recordData: Record<string, any> = {
      field_name: formData.Name__c,
      field_email: formData.Email__c,
      field_phone: formData.Phone__c,
      field_status: formData.Status__c,
      field_company: formData.Company__c,
      field_industry: formData.Industry__c,
      field_rating: formData.Rating__c,
      field_lead_source: formData.LeadSource__c,
    };

    const validationResult = validateRecord('Lead__c', recordData);

    if (!validationResult.isValid) {
      setValidationRuleErrors(validationResult.errors);
      showError('Validation Rule Error', 'Record does not meet validation requirements');
      return;
    }

    console.log('[LeadModal] Calling saveMutation.mutate with:', formData);
    saveMutation.mutate(formData);
  };

  return (
    <TabbedModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Lead' : 'New Lead'}
      tabs={tabs}
      maxWidth="2xl"
      actions={
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#334155] rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saveMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending
              ? 'Saving...'
              : isEditMode ? 'Save Changes' : 'Create Lead'
            }
          </button>
        </div>
      }
    >
      {(activeTab) => (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Validation Rule Errors */}
          {validationRuleErrors.length > 0 && (
            <ValidationErrors
              errors={validationRuleErrors}
              onDismiss={() => setValidationRuleErrors([])}
              className="mb-4"
            />
          )}

          {activeTab === 'overview' && (
            <div className="max-h-[60vh] overflow-y-auto pr-2">
              {loadingLayout ? (
                <div className="space-y-6">
                  <div className="animate-pulse space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i}>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2"></div>
                        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : pageLayout?.sections && pageLayout.sections.length > 0 ? (
                pageLayout.sections.map(section => (
                  <EditSection
                    key={section.id}
                    section={section}
                    objectApiName="Lead__c"
                    formData={formData}
                    onChange={handleFieldChange}
                    errors={errors}
                    disabled={saveMutation.isPending}
                  />
                ))
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>No page layout configured for Lead.</p>
                  <p className="text-sm mt-2">Go to Object Manager to configure page layouts.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'related' && lead?.id && (
            <RelatedRecordsTab
              recordId={lead.id}
              objectType="Lead__c"
              recordName={lead.name as string}
            />
          )}

          {activeTab === 'activity' && lead?.id && (
            <ActivityTab
              recordId={lead.id}
              objectType="Lead__c"
              recordName={lead.name as string}
            />
          )}
        </form>
      )}
    </TabbedModal>
  );
};
